// -----------------------------------------------------------------------------
// Date display / parse helpers.
//
// Dates are STORED as ISO date-only strings ("YYYY-MM-DD" — the domain contract,
// see plans/CMS.md §3) and only ever DISPLAYED in European format (dd/MM/yyyy).
// Keep storage ISO; never persist the European string, or the lexicographic
// date comparisons in src/lib/cms.ts (overdue, sorting) break.
//
// These helpers are timezone-safe: they work on the local calendar date and
// never route a date-only string through `new Date("YYYY-MM-DD")`, which parses
// as UTC midnight and can shift the day in negative-offset timezones.
// -----------------------------------------------------------------------------

/** Human-facing European date pattern — used for input placeholders/labels. */
export const EURO_DATE_PATTERN = "dd/mm/yyyy";

// Matches a leading "YYYY-MM-DD" — works for both date-only strings and full ISO
// timestamps ("YYYY-MM-DDTHH:mm:ss.sssZ", e.g. createdAt/updatedAt).
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

/**
 * Format an ISO date string for display in European format (dd/MM/yyyy).
 * Accepts a date-only string ("YYYY-MM-DD") or a full ISO timestamp (only the
 * calendar date is shown). Returns "" for null/empty/unparseable input so
 * callers can render the result directly.
 */
export function formatEuroDate(value: string | null | undefined): string {
    if (!value) return "";
    const m = ISO_DATE_RE.exec(value);
    if (!m) return "";
    const [, year, month, day] = m;
    return `${day}/${month}/${year}`;
}

/**
 * Parse an ISO date-only string ("YYYY-MM-DD") into a LOCAL `Date` at midnight,
 * for feeding the calendar's `selected` / `defaultMonth`. Returns `undefined`
 * for null/empty/unparseable input, and for impossible dates that would roll
 * over (e.g. "2026-02-31"). Builds the date from local components on purpose —
 * `new Date(string)` would parse as UTC and could shift the day.
 */
export function parseDateOnly(value: string | null | undefined): Date | undefined {
    if (!value) return undefined;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return undefined;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const date = new Date(year, month - 1, day);
    // Reject JS Date roll-over (e.g. month 13, or Feb 31 -> Mar).
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return undefined;
    }
    return date;
}

/**
 * Format a `Date` as an ISO date-only string ("YYYY-MM-DD") from its LOCAL
 * calendar components — matching `todayLocal()` in companiesApi. This is the
 * value to store when a user picks a date.
 */
export function toDateOnly(date: Date): string {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}
